/* eslint-disable sort-imports */
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionType } from "../../../AppDataProvider/actions";
import { AppContext, BookingData } from "../../../AppDataProvider/AppDataProvider";
import { AdyenBrowserInfo, CreateAdyenSessionMutation, CreateBookingWithAdyenInvoiceMutationVariables, useCreateAdyenSessionMutation, useCreateBookingWithAdyenInvoiceMutation } from "../../../GeneratedGraphQL";
import { getBookingQuestionInput, getCrmFieldBookingInput, requireBookingDataFields } from "../../../utils";
import { PartRequired } from "../../../utils/types";
import * as Adyen from "./Adyen";
import { useScript } from "./useScript";
declare class Dropin {
  constructor(options: unknown);
  mount(selector: HTMLDivElement | string): void;
  setStatus(status: string): void;
  unmount(): void;
}
declare global {
  const AdyenWeb: {
    AdyenCheckout: (options: unknown) => Promise<unknown>;
    Dropin: Dropin;
  } | undefined;
}

/**
 * We should replace this with a more robust logging solution in the future.
 */
const logger: Pick<typeof console, "debug" | "error" | "info" | "warn"> = process.env.RUNTIME_ENV?.toLowerCase() === "production" ? {
  debug() {
    /* noop */
  },
  error() {
    /* noop */
  },
  info() {
    /* noop */
  },
  warn() {
    /* noop */
  }
} : console;
type PaymentsStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "reservedSpaceId" | "service" | "timezone" | "userDetails">;
type AdyenPaymentProps = {
  onComplete(bookingIds: string[]): void;
  value: number;
};
export function AdyenPayment({
  onComplete,
  value
}: AdyenPaymentProps): React.ReactElement {
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  requireBookingDataFields<PaymentsStepBookingData>(appData.bookingData, ["createdSource", "organisation", "reservedSpaceId", "timezone", "userDetails", "service"]);
  const {
    organisation
  } = appData.bookingData;
  const {
    i18n,
    t
  } = useTranslation();
  const [loading, setLoading] = useState(true);
  const adyenScriptStatus = useScript(`https://checkoutshopper-${Adyen.getEnvFromCheckoutUrl(appData.bookingData.organisation.adyenIntegrationToken?.adyenCheckoutBaseUrl)}.cdn.adyen.com/checkoutshopper/sdk/6.2.0/adyen.js`);
  const [adyenCheckout, setAdyenCheckout] = useState<unknown | null>(null);
  const [adyenDropIn, setAdyenDropIn] = useState<Dropin | null>(null);
  const [adyenSession, setAdyenSession] = useState<CreateAdyenSessionMutation | null>(null);
  const adyenDropInContainerRef = useRef<HTMLDivElement>(null);
  const catchError = useCallback((error: unknown) => {
    logger.error(error);
    setLoading(false);
    appDataDispatch({
      payload: {
        buttonLabel: t("tryAgain"),
        content: t("errors.paymentError"),
        icon: faExclamationTriangle,
        onClick() {
          window.location.reload();
        },
        title: t("serverError")
      },
      type: ActionType.SHOW_MODAL_DIALOG
    });
  }, [appDataDispatch, t]);
  const [createAdyenSession] = useCreateAdyenSessionMutation({
    variables: {
      countryCode: appData.userLocation ?? "GB",
      currency: appData.bookingData.organisation.currency,
      locale: i18n.language,
      organisationId: appData.bookingData.organisation._id,
      value
    }
  });
  const presetCreateBookingWithAdyenInvoiceVariables = useMemo<Omit<CreateBookingWithAdyenInvoiceMutationVariables, "bookingToolId" | "browserInfo" | "paymentMethodData"> & {
    bookingToolId?: string;
  }>(() => {
    requireBookingDataFields<PaymentsStepBookingData>(appData.bookingData, ["createdSource", "organisation", "reservedSpaceId", "timezone", "userDetails", "service"]);
    return {
      ...(!appData.bookingTool.isGenericBookingTool && {
        bookingToolId: appData.bookingTool._id
      }),
      bookings: [{
        ...(appData.bookingData.bookingNotes && {
          bookingNotes: appData.bookingData.bookingNotes
        }),
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: appData.bookingData.bookingQuestionData,
          bookingQuestions: organisation.externalBookingQuestions,
          pickedServiceId: appData.bookingData.service._id
        }),
        customer: {
          ...appData.bookingData.userDetails,
          crmFieldData: getCrmFieldBookingInput(appData.bookingData.userDetails.crmFieldData)
        },
        organisationId: organisation._id,
        reservedSpaceId: appData.bookingData.reservedSpaceId,
        timezone: appData.bookingData.timezone
      }],
      createdSource: appData.bookingData.createdSource,
      locale: i18n.language,
      origin: window.location.origin,
      paymentAuthenticationData: {
        threeDSRequestData: {
          nativeThreeDS: "preferred"
        }
      },
      paymentCurrency: organisation.currency,
      paymentValue: value,
      ...(appData.bookingData.discountCode && {
        paymentDiscount: {
          customerEmail: appData.bookingData.userDetails.email,
          discountCode: appData.bookingData.discountCode
        }
      })
    };
  }, [appData.bookingData, appData.bookingTool._id, appData.bookingTool.isGenericBookingTool, i18n.language, organisation._id, organisation.currency, organisation.externalBookingQuestions, value]);
  const [createBookingsWithAdyenInvoice] = useCreateBookingWithAdyenInvoiceMutation();

  /**
   * If we navigate away but we're still mounted, reset everything.
   */
  useEffect(() => {
    if (appData.currentStep !== "payments" && (adyenCheckout || adyenDropIn || adyenSession)) {
      logger.debug("Navigated away from payments step with AdyenPayment state setup, resetting AdyenPayment step", {
        currentStep: appData.currentStep
      });
      setLoading(true);
      adyenDropIn?.unmount();
      setAdyenSession(null);
      setAdyenDropIn(null);
      setAdyenCheckout(null);
    }
  }, [adyenCheckout, adyenDropIn, adyenSession, appData.currentStep]);
  useEffect(() => {
    if (!adyenSession) {
      createAdyenSession().then(result => {
        logger.debug("Initialised Adyen session", result);
        if (result.data) {
          setAdyenSession(result.data);
        } else {
          catchError(new Error("Expected 'data' in result of createAdyenSession mutation to be defined"));
        }
      }).catch(error => {
        if (error instanceof Error) {
          catchError(error);
        }
      });
    }
  }, [adyenSession, catchError, createAdyenSession]);
  useEffect(() => {
    (async () => {
      if (adyenScriptStatus && typeof AdyenWeb !== "undefined" && adyenSession && !adyenCheckout) {
        const session = adyenSession.createAdyenSession;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const paymentMethods = JSON.parse(session.paymentMethods);
        const adyenCheckoutConfiguration = {
          amount: {
            currency: organisation.currency,
            value
          },
          clientKey: session.clientKey,
          countryCode: appData.userLocation,
          environment: organisation.adyenIntegrationToken?.adyenCheckoutBaseUrl.includes("test") ? "test" : "live",
          /**
           * For some reason Adyen refuses to provide en-GB as a locale so we
           * have to use en-US instead.
           */
          locale: i18n.language?.toLowerCase() === "en-gb" ? "en-US" : i18n.language,
          /**
           * Handles when a payment method requires more details, for example for
           * native 3D Secure 2.
           */
          onAdditionalDetails(state: {
            data: {
              details: {
                threeDSResult: string;
              };
            };
          }, component: {
            invoiceId: string;
          }, actions: {
            reject: () => void;
            resolve: (options: {
              resultCode?: string;
            }) => void;
          }) {
            logger.debug("adyenCheckout.onAdditionalDetails()", {
              actions,
              component,
              state
            });
            createBookingsWithAdyenInvoice({
              variables: {
                ...presetCreateBookingWithAdyenInvoiceVariables,
                payment3DSResult: state.data.details.threeDSResult,
                paymentInvoiceId: component.invoiceId
              }
            }).then(result => {
              const resultCode = result.data?.createBookingsWithAdyenInvoice.resultCode ?? undefined;
              if (resultCode === "Authorised" || resultCode === "Received") {
                const bookingIds = result.data?.createBookingsWithAdyenInvoice.bookings.map(booking => booking._id) ?? [];
                Object.assign(component, {
                  bookingIds
                });
              }
              actions.resolve({
                resultCode
              });
            }).catch(() => {
              actions.reject();
            });
          },
          onError(error: Error) {
            catchError(error);
          },
          onPaymentCompleted(result: unknown, component: {
            bookingIds: string[];
          }) {
            logger.debug("adyenCheckout.onPaymentCompleted()", {
              component,
              result
            });
            onComplete(component.bookingIds);
          },
          onPaymentFailed(result: unknown, component: {
            bookingIds: string[];
          }) {
            logger.debug("adyenCheckout.onPaymentFailed()", {
              component,
              result
            });
            appDataDispatch({
              payload: {
                buttonLabel: t("tryAgain"),
                content: t("adyen.errors.paymentRefusedDescription"),
                icon: faExclamationTriangle,
                onClick() {
                  appDataDispatch({
                    type: ActionType.BACK_FROM_PAYMENTS_STEP
                  });
                },
                title: t("adyen.errors.paymentRefusedTitle")
              },
              type: ActionType.SHOW_MODAL_DIALOG
            });
          },
          onSubmit(state: {
            data: {
              browserInfo: AdyenBrowserInfo;
              paymentMethod: unknown;
            };
          }, component: {
            handleAction(action: unknown, props?: unknown): void;
          }, actions: {
            reject: () => void;
            resolve: (options: {
              resultCode?: string;
            }) => void;
          }) {
            logger.debug("adyenCheckout.onSubmit()", {
              actions,
              component,
              state
            });
            createBookingsWithAdyenInvoice({
              variables: {
                ...presetCreateBookingWithAdyenInvoiceVariables,
                browserInfo: state.data.browserInfo,
                paymentMethodData: JSON.stringify(state.data.paymentMethod)
              }
            }).then(result => {
              const resultCode = result.data?.createBookingsWithAdyenInvoice.resultCode ?? undefined;
              const action = result.data?.createBookingsWithAdyenInvoice.action;

              /**
               * If the action object isn't null in the response there's an
               * additional step that needs to be handled by the component
               * before we can proceed.
               *
               * See the onAdditionalDetails() method above.
               */
              if (action) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const parsedAction = JSON.parse(action);
                const invoiceId = result.data?.createBookingsWithAdyenInvoice.invoiceId;
                logger.debug("Handling additional action", {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  action: parsedAction,
                  invoiceId
                });

                /**
                 * A little bit of a hack but I can't find an official way to
                 * in the Adyen documentation to pass a state variable to the
                 * onAdditionalDetails() method.
                 *
                 * Here I'm attaching it to the component object so it can be
                 * accessed that way.
                 */
                Object.assign(component, {
                  invoiceId
                });
                component.handleAction(parsedAction);
                return;
              }
              if (resultCode === "Authorised" || resultCode === "Received") {
                const bookingIds = result.data?.createBookingsWithAdyenInvoice.bookings.map(booking => booking._id) ?? [];
                Object.assign(component, {
                  bookingIds
                });
              }
              actions.resolve({
                resultCode
              });
            }).catch(error => {
              logger.error(error);
              actions.reject();
            });
          },
          paymentMethodsResponse: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            paymentMethods
          }
        };
        logger.debug("Initialising Adyen Checkout instance", adyenCheckoutConfiguration);
        const adyenCheckout = await AdyenWeb.AdyenCheckout(adyenCheckoutConfiguration);
        logger.debug("Successfully initialised Adyen Checkout instance", adyenCheckout);
        setAdyenCheckout(adyenCheckout);
      }
    })().catch(catchError);
  }, [adyenCheckout, adyenDropIn, adyenScriptStatus, adyenSession, appData.userLocation, appDataDispatch, catchError, createBookingsWithAdyenInvoice, i18n.language, onComplete, organisation.adyenIntegrationToken?.adyenCheckoutBaseUrl, organisation.currency, presetCreateBookingWithAdyenInvoiceVariables, t, value]);
  useEffect(() => {
    if (adyenScriptStatus && typeof AdyenWeb !== "undefined" && adyenSession && adyenCheckout && adyenDropInContainerRef.current && !adyenDropIn) {
      // Hack to get around TypeScript thinking it's not a constructor for
      // some reason.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const adyenDropIn = (new AdyenWeb.Dropin(adyenCheckout) as Dropin);
      adyenDropIn.mount(adyenDropInContainerRef.current);
      logger.debug("Mounted Adyen Drop-In", adyenDropIn);

      /**
       * This is required for the scenario where we re-mount it during a retry
       * payment flow after a refused payment.
       */
      adyenDropIn.setStatus("ready");
      setLoading(false);
    }
  }, [adyenCheckout, adyenDropIn, adyenScriptStatus, adyenSession]);
  return <>
      {loading && <div>Loading...</div>}

      {/* This div needs to always be in the DOM so we don't have any issues
       mounting the Adyen Drop-In onto it. */}
      <div id="adyen-drop-in" ref={adyenDropInContainerRef} />
    </>;
}